// Mock host-context presets for the focus-mode preview.
//
// Focus mode renders a single slot (or modal) standalone, against a fake
// `BlockHostContext` so the operator can see edits without dragging real
// products into the 3D scene. Presets are named ("default" /
// "selection-present" / "cart-with-items") and the embed URL picks one via
// `?mockState=`.
//
// Each preset takes the *real* fetched catalog (products + categories) and
// returns a synthesised BlockHostContext that represents a plausible scene
// state. The catalog is needed because blocks like `material-collections`
// and `catalog` read from it; we don't fake the catalog itself, just the
// scene state on top.

import type { BlockHostContext } from "../blocks/types";
import type {
  DesignerSeriesDto,
  SceneCategoryDto,
  SceneInstance,
  SceneProductDetailResponse,
  SceneProductDto,
} from "../types";
import type { ActionDef } from "../actions/types";

export type MockStatePreset =
  | "default"
  | "selection-present"
  | "cart-with-items";

export const MOCK_STATE_PRESETS: MockStatePreset[] = [
  "default",
  "selection-present",
  "cart-with-items",
];

export function isMockStatePreset(value: string | null | undefined): value is MockStatePreset {
  return value != null && (MOCK_STATE_PRESETS as string[]).includes(value);
}

export interface BuildMockHostArgs {
  preset: MockStatePreset;
  /** The active series the embed loaded against. May be null in demo mode. */
  activeSeries: DesignerSeriesDto | null;
  /** Catalog products visible to this Designer. */
  products: SceneProductDto[];
  /** Catalog categories visible to this Designer (scoped to active series). */
  visibleCategories: SceneCategoryDto[];
  /** Pre-resolved theme tokens (for getFeature contract; unused for now). */
  baseUrl?: string;
  /** Decimal separator ("." or ",") the config resolved — so the preview's
   *  measurement readout matches what ships. Defaults to ".". */
  decimalSeparator?: string;
}

/**
 * Build a BlockHostContext suitable for previewing one slot/modal in
 * isolation. The actions map is intentionally empty — actions in the
 * preview are visible but inert. If an operator clicks one, nothing
 * happens (no Unity instance, no real cart, etc.).
 */
export function buildMockHost(args: BuildMockHostArgs): BlockHostContext {
  const { preset, activeSeries, products, visibleCategories, baseUrl } = args;
  const decimalSeparator = args.decimalSeparator === "," ? "," : ".";

  const productById = new Map<string, SceneProductDto>();
  for (const p of products) productById.set(p.id, p);

  const instances = buildMockInstances(preset, products);
  const selectedInstance = preset === "selection-present" && instances[0]
    ? instances[0]
    : null;

  // Seed a mock product-detail per product the instances reference, with two
  // generic surfaces + materials, and point the instances' picks at them. This
  // is what lets the product-materials block resolve to display names in the
  // preview (it reads surfaceLabel + materialName off the detail cache). Names
  // are generic placeholders so the preview reads for any project.
  const detailCache = new Map<string, SceneProductDetailResponse>();
  for (const inst of instances) {
    if (!detailCache.has(inst.productId)) {
      detailCache.set(inst.productId, mockProductDetail(inst.productId));
    }
    // Pick the first material on each surface so the block has something to show.
    const detail = detailCache.get(inst.productId)!;
    inst.materialPicks = {};
    for (const s of detail.surfaces) {
      if (s.materials[0]) inst.materialPicks[s.surfaceKey] = s.materials[0].id;
    }
  }

  // Stub action map — all actions render as disabled buttons in the
  // preview. The operator sees their position + icon but can't drive them.
  // Build as a mutable Map; ActionMap is ReadonlyMap so we widen at the
  // assignment site rather than mutate a readonly type.
  const stubActions = new Map<string, ActionDef>();
  // Provide a few well-known ids so layouts that filter on action presence
  // (e.g. only show a button when the action exists) still render them.
  for (const id of [
    "reset", "delete", "undo", "redo", "rotate-clockwise", "rotate-counter-clockwise",
    "camera-toggle", "measurements-toggle", "help", "open-cart", "close-cart",
    "back-to-series",
    "open-scene-looks", "apply-scene-materials", "download-model", "render-scene",
    // Cart-footer verbs — so the cart section preview shows the PDF buttons
    // (an unknown action id renders nothing, blanking the footer otherwise).
    "render-pdf-download", "render-pdf-email", "send-email-from-cart",
  ]) {
    stubActions.set(id, {
      id,
      // Real ActionDef label (mirrors useHostActions) so icon-only buttons show
      // a meaningful tooltip in the preview instead of the raw action id.
      label: stubLabelFor(id),
      icon: stubIconFor(id),
      enabled: true,
      run: () => { /* inert in preview */ },
    });
  }

  const selectedProductName = selectedInstance
    ? productById.get(selectedInstance.productId)?.name ?? null
    : null;
  return {
    actions: stubActions,
    selectedInstanceId: selectedInstance?.instanceId ?? null,
    selectedInstance,
    selectedProductName,
    instances,
    instanceCount: instances.length,
    productById,
    visibleCategories,
    products,
    activeSeries,
    detailCache,
    // Static preview cache — pre-filled once, never warms further. A non-zero
    // constant satisfies blocks that key memoisation on the version.
    detailCacheVersion: 1,
    onCacheDetail: () => { /* preview doesn't fetch detail */ },
    getFeature: featureKey => {
      // The resolved decimal separator applies in every preset so the readout
      // punctuation matches what ships regardless of scene state.
      if (featureKey === "decimalSeparator") return decimalSeparator;
      // A couple of plausible defaults so price/measurement blocks render
      // sensibly when previewed under the cart-with-items preset.
      if (preset === "cart-with-items") {
        if (featureKey === "totalPrice") return { amount: 12450, currency: "DKK" };
        // Plausible scene bounds (metres) so the measurement stat in the cart
        // totals slot renders a real "W × H" value in the composite preview
        // instead of collapsing to null.
        if (featureKey === "sceneDimensions") return { width: 1.8, height: 0.72, depth: 0.4 };
      }
      return null;
    },
    openModal: () => { /* inert in preview */ },
    openConfirm: () => { /* inert in preview */ },
    baseUrl,
    applyMaterialToAll: () => { /* inert in preview */ },
    onPickMaterial: () => { /* inert in preview */ },
    collectionPicks: {},
    onPickCollectionDefault: () => { /* inert in preview */ },
    formValues: {},
    setFormValue: () => { /* inert in preview */ },
    readFormValues: () => ({}),
    clearForm: () => { /* inert in preview */ },
  };
}

function buildMockInstances(preset: MockStatePreset, products: SceneProductDto[]): SceneInstance[] {
  if (preset === "default") return [];
  if (products.length === 0) return [];

  if (preset === "selection-present") {
    return [
      {
        instanceId: "preview-instance-1",
        productId: products[0].id,
        materialPicks: {},
      },
    ];
  }

  // cart-with-items: 2 copies of the first product + 1 of the second (or
  // 3 copies of the first if there's only one product).
  const second = products[1] ?? products[0];
  return [
    { instanceId: "preview-instance-1", productId: products[0].id, materialPicks: {} },
    { instanceId: "preview-instance-2", productId: products[0].id, materialPicks: {} },
    { instanceId: "preview-instance-3", productId: second.id, materialPicks: {} },
  ];
}

/** A synthetic product detail with two generic surfaces, each with a couple of
 *  generic materials — enough for the product-materials block + the selection
 *  surface picker to render in preview. Names are project-neutral placeholders
 *  (not the real DK3 catalog) so the preview reads for any project. */
function mockProductDetail(productId: string): SceneProductDetailResponse {
  const mat = (key: string, name: string) => ({
    id: `${productId}:${key}`,
    dmatAssetId: null,
    name,
    swatchUrl: null,
  });
  return {
    id: productId,
    name: "Sample product",
    surfaces: [
      {
        surfaceKey: "body",
        surfaceLabel: "Body",
        locked: false,
        defaultMaterialId: `${productId}:oak`,
        materials: [mat("oak", "Oak"), mat("walnut", "Walnut")],
      },
      {
        surfaceKey: "front",
        surfaceLabel: "Front",
        locked: false,
        defaultMaterialId: `${productId}:soap`,
        materials: [mat("soap", "Soap-treated"), mat("white", "White")],
      },
    ],
  };
}

/** Real ActionDef labels (mirrors useHostActions) so the preview's icon-only
 *  tool-rail buttons get a proper tooltip instead of the raw action id. */
function stubLabelFor(id: string): string {
  switch (id) {
    case "reset":                    return "Reset scene";
    case "delete":                   return "Delete selection";
    case "undo":                     return "Undo";
    case "redo":                     return "Redo";
    case "rotate-clockwise":         return "Rotate clockwise";
    case "rotate-counter-clockwise": return "Rotate counter-clockwise";
    case "camera-toggle":            return "Camera toggle";
    case "measurements-toggle":      return "Show measurements";
    case "help":                     return "Help";
    case "open-cart":                return "View element list";
    case "close-cart":               return "Close";
    case "back-to-series":           return "Back to series";
    case "open-scene-looks":         return "Scene looks";
    case "apply-scene-materials":    return "Apply scene look";
    case "download-model":           return "Download 3D model";
    case "render-scene":             return "Render scene";
    case "render-pdf-download":      return "Download PDF";
    case "render-pdf-email":         return "Email PDF";
    case "send-email-from-cart":     return "Email";
    default:                         return id;
  }
}

function stubIconFor(id: string): string {
  switch (id) {
    case "reset":                          return "refresh";
    case "delete":                         return "delete";
    case "undo":                           return "undo";
    case "redo":                           return "redo";
    case "rotate-clockwise":               return "rotate_right";
    case "rotate-counter-clockwise":       return "rotate_left";
    case "camera-toggle":                  return "landscape";
    case "measurements-toggle":            return "straighten";
    case "help":                           return "help_outline";
    case "open-cart":                      return "list_alt";
    case "close-cart":                     return "close";
    case "back-to-series":                 return "arrow_back";
    case "open-scene-looks":               return "palette";
    case "apply-scene-materials":          return "format_paint";
    case "download-model":                 return "download";
    case "render-scene":                   return "photo_camera";
    case "render-pdf-download":            return "picture_as_pdf";
    case "render-pdf-email":               return "mail";
    case "send-email-from-cart":           return "mail";
    default:                               return "circle";
  }
}
